import { PythonTopicExercise } from '../../../../types/python-topic';

export const aleatoriedadExercises: PythonTopicExercise[] = [
  {
    id: 1,
    title: 'Generar números aleatorios',
    description: 'Aprende a generar números aleatorios usando el módulo random de Python.',
    initialCode: `import random

# Genera un número aleatorio entre 1 y 10
numero_aleatorio = 

# Genera un número aleatorio entre 0 y 1
decimal_aleatorio = 

# Elige un elemento aleatorio de una lista
colores = ["rojo", "verde", "azul", "amarillo"]
color_aleatorio = 

print(f"Número aleatorio (1-10): {numero_aleatorio}")
print(f"Decimal aleatorio (0-1): {decimal_aleatorio}")
print(f"Color aleatorio: {color_aleatorio}")`,
    solution: `import random

# Genera un número aleatorio entre 1 y 10
numero_aleatorio = random.randint(1, 10)

# Genera un número aleatorio entre 0 y 1
decimal_aleatorio = random.random()

# Elige un elemento aleatorio de una lista
colores = ["rojo", "verde", "azul", "amarillo"]
color_aleatorio = random.choice(colores)

print(f"Número aleatorio (1-10): {numero_aleatorio}")
print(f"Decimal aleatorio (0-1): {decimal_aleatorio}")
print(f"Color aleatorio: {color_aleatorio}")`,
    testCases: [],
    hints: [
      'Usa random.randint(a, b) para números enteros',
      'Usa random.random() para decimales entre 0 y 1',
      'Usa random.choice(lista) para elegir de una lista',
      'No olvides importar random al inicio'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué generar números aleatorios?',
      content: `Los **números aleatorios** son esenciales para simulaciones, juegos, muestreo de datos y muchas aplicaciones prácticas.

### ¿Por qué este ejercicio?
- **Simulaciones**: Modelar eventos aleatorios
- **Juegos**: Generar resultados aleatorios
- **Muestreo**: Seleccionar datos aleatorios
- **Aplicación práctica**: Muy común en programación

### En el mundo real:
Cuando un sistema de sorteos necesita seleccionar un ganador aleatorio, usa random.choice() para elegir de una lista de participantes. Sin aleatoriedad, no podrías hacer sorteos justos.`,
      keyPoints: [
        'random.randint(a, b) genera enteros en un rango',
        'random.random() genera decimales entre 0 y 1',
        'random.choice() elige elementos aleatorios de listas',
        'La aleatoriedad es esencial para muchas aplicaciones'
      ],
      realWorldExample: 'En un sistema de recomendaciones, se muestran productos aleatorios cuando no hay preferencias: random.choice(productos).'
    },
    theory: `# Generar Números Aleatorios

## Módulo random

Python incluye el módulo \`random\` para generar números aleatorios.

## Funciones Principales

### randint(a, b)
Genera un entero aleatorio entre a y b (inclusive):

\`\`\`python
import random
numero = random.randint(1, 10)  # Entre 1 y 10
\`\`\`

### random()
Genera un decimal aleatorio entre 0.0 y 1.0:

\`\`\`python
import random
decimal = random.random()  # Entre 0.0 y 1.0
\`\`\`

### choice(lista)
Elige un elemento aleatorio de una lista:

\`\`\`python
import random
colores = ["rojo", "verde", "azul"]
color = random.choice(colores)
\`\`\`

## Aplicaciones

- **Simulaciones**: Modelar eventos aleatorios
- **Juegos**: Resultados aleatorios
- **Muestreo**: Seleccionar datos aleatorios
- **Testing**: Generar datos de prueba`,
    tips: [
      'Importa random al inicio: import random',
      'randint(a, b) incluye ambos extremos',
      'random() genera entre 0.0 y 1.0',
      'choice() elige de listas, tuplas o strings',
      'Puedes usar seed() para resultados reproducibles'
    ],
    advice: [
      'Usa random para simulaciones y juegos',
      'Considera usar seed() para debugging',
      'Aprende las diferentes funciones de random',
      'La aleatoriedad es útil para testing'
    ]
  },
  {
    id: 2,
    title: 'Mezclar listas con shuffle',
    description: 'Aprende a mezclar elementos de una lista aleatoriamente usando shuffle().',
    initialCode: `import random

# Lista de cartas
cartas = ["As", "Rey", "Reina", "Jota", "10", "9"]

# Mezcla la lista de cartas aleatoriamente
# Usa random.shuffle(lista)
# Nota: shuffle() modifica la lista original

print(f"Cartas mezcladas: {cartas}")`,
    solution: `import random

# Lista de cartas
cartas = ["As", "Rey", "Reina", "Jota", "10", "9"]

# Mezcla la lista de cartas aleatoriamente
# Usa random.shuffle(lista)
# Nota: shuffle() modifica la lista original
random.shuffle(cartas)

print(f"Cartas mezcladas: {cartas}")`,
    testCases: [],
    hints: [
      'random.shuffle(lista) mezcla la lista in-place',
      'shuffle() modifica la lista original',
      'No devuelve nada, solo modifica la lista',
      'Útil para barajar cartas, ordenar aleatoriamente, etc.'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué mezclar listas?',
      content: `**Mezclar listas** es útil para barajar cartas, ordenar aleatoriamente elementos, crear variaciones de datos, y muchas aplicaciones prácticas.

### ¿Por qué este ejercicio?
- **Juegos**: Barajar cartas, dados, etc.
- **Muestreo**: Crear variaciones aleatorias de datos
- **Aplicación práctica**: Muy común en juegos y simulaciones
- **Aleatoriedad**: Generar orden aleatorio

### En el mundo real:
Cuando un sistema de juegos baraja un mazo de cartas, usa shuffle() para mezclar aleatoriamente. Sin shuffle(), tendrías que implementar tu propio algoritmo de mezcla.`,
      keyPoints: [
        'random.shuffle(lista) mezcla in-place',
        'Modifica la lista original',
        'No devuelve nada, solo modifica',
        'Útil para barajar y ordenar aleatoriamente'
      ],
      realWorldExample: 'En un sistema de preguntas de examen, shuffle() mezcla el orden de las preguntas para evitar que todos tengan el mismo orden.'
    },
    theory: `# Mezclar Listas con shuffle()

## ¿Qué es shuffle()?

**shuffle()** mezcla los elementos de una lista aleatoriamente, modificando la lista original.

## Sintaxis

\`\`\`python
import random
cartas = ["As", "Rey", "Reina"]
random.shuffle(cartas)  # Mezcla cartas in-place
\`\`\`

## Características

### Modifica In-Place
\`shuffle()\` modifica la lista original:
\`\`\`python
cartas = ["As", "Rey", "Reina"]
random.shuffle(cartas)
print(cartas)  # Orden aleatorio
\`\`\`

### No Devuelve Nada
\`shuffle()\` no devuelve una nueva lista:
\`\`\`python
resultado = random.shuffle(cartas)  # None
\`\`\`

## Aplicaciones

- **Juegos**: Barajar cartas, dados
- **Muestreo**: Crear variaciones aleatorias
- **Testing**: Generar datos de prueba aleatorios`,
    tips: [
      'random.shuffle(lista) mezcla in-place',
      'Modifica la lista original',
      'No devuelve nada, solo modifica',
      'Útil para barajar y ordenar aleatoriamente',
      'Si necesitas mantener la original, crea una copia primero'
    ],
    advice: [
      'Usa shuffle() para barajar elementos',
      'Recuerda que modifica la lista original',
      'Crea una copia si necesitas mantener la original',
      'Practica en contextos de juegos y simulaciones'
    ]
  },
  {
    id: 3,
    title: 'Seleccionar muestra aleatoria con sample',
    description: 'Aprende a seleccionar una muestra aleatoria de elementos de una lista usando sample().',
    initialCode: `import random

# Lista de estudiantes
estudiantes = ["Ana", "Juan", "María", "Pedro", "Luis", "Sofía"]

# Selecciona 3 estudiantes aleatorios
# Usa random.sample(lista, k) donde k es el número de elementos
muestra = 

print(f"Muestra aleatoria: {muestra}")`,
    solution: `import random

# Lista de estudiantes
estudiantes = ["Ana", "Juan", "María", "Pedro", "Luis", "Sofía"]

# Selecciona 3 estudiantes aleatorios
# Usa random.sample(lista, k) donde k es el número de elementos
muestra = random.sample(estudiantes, 3)

print(f"Muestra aleatoria: {muestra}")`,
    testCases: [],
    hints: [
      'random.sample(lista, k) selecciona k elementos únicos',
      'No repite elementos en la muestra',
      'k no puede ser mayor que la longitud de la lista',
      'Devuelve una nueva lista, no modifica la original'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar sample()?',
      content: `**sample()** permite seleccionar una muestra aleatoria de elementos sin repetición, útil para sorteos, muestreo estadístico y selección aleatoria.

### ¿Por qué este ejercicio?
- **Muestreo**: Seleccionar muestras representativas
- **Sorteos**: Elegir ganadores sin repetición
- **Aplicación práctica**: Muy común en estadística y juegos
- **Sin repetición**: Garantiza elementos únicos

### En el mundo real:
Cuando un sistema de sorteos necesita elegir 3 ganadores de 100 participantes, usa sample() para garantizar que no haya repetición. Sin sample(), tendrías que implementar lógica para evitar duplicados.`,
      keyPoints: [
        'random.sample(lista, k) selecciona k elementos únicos',
        'No repite elementos en la muestra',
        'k no puede ser mayor que len(lista)',
        'Devuelve una nueva lista'
      ],
      realWorldExample: 'En un sistema de encuestas, sample() selecciona una muestra aleatoria de usuarios para enviar encuestas.'
    },
    theory: `# Seleccionar Muestra con sample()

## ¿Qué es sample()?

**sample()** selecciona una muestra aleatoria de elementos sin repetición.

## Sintaxis

\`\`\`python
import random
estudiantes = ["Ana", "Juan", "María"]
muestra = random.sample(estudiantes, 2)  # 2 elementos únicos
\`\`\`

## Características

### Sin Repetición
\`sample()\` no repite elementos:
\`\`\`python
muestra = random.sample([1, 2, 3, 4], 2)  # Siempre 2 elementos únicos
\`\`\`

### Límite
k no puede ser mayor que la longitud:
\`\`\`python
random.sample([1, 2], 3)  # Error: k > len(lista)
\`\`\`

## Aplicaciones

- **Muestreo estadístico**: Seleccionar muestras
- **Sorteos**: Elegir ganadores sin repetición
- **Testing**: Seleccionar casos de prueba aleatorios`,
    tips: [
      'random.sample(lista, k) selecciona k elementos únicos',
      'No repite elementos en la muestra',
      'k no puede ser mayor que len(lista)',
      'Devuelve una nueva lista',
      'Útil para sorteos y muestreo'
    ],
    advice: [
      'Usa sample() cuando necesites elementos únicos',
      'Recuerda que k no puede ser mayor que la longitud',
      'Practica en contextos de sorteos y muestreo',
      'Compara con choice() que puede repetir'
    ]
  },
  {
    id: 4,
    title: 'Generar números aleatorios con uniform',
    description: 'Aprende a generar números decimales aleatorios en un rango específico usando uniform().',
    initialCode: `import random

# Genera un número decimal aleatorio entre 10.0 y 20.0
# Usa random.uniform(a, b) donde a y b son los límites
precio_aleatorio = 

print(f"Precio aleatorio: {precio_aleatorio}")`,
    solution: `import random

# Genera un número decimal aleatorio entre 10.0 y 20.0
# Usa random.uniform(a, b) donde a y b son los límites
precio_aleatorio = random.uniform(10.0, 20.0)

print(f"Precio aleatorio: {precio_aleatorio}")`,
    testCases: [],
    hints: [
      'random.uniform(a, b) genera decimales en un rango',
      'a y b pueden ser cualquier número decimal',
      'El resultado puede ser a, b o cualquier valor entre ellos',
      'Útil para precios, coordenadas, medidas, etc.'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar uniform()?',
      content: `**uniform()** permite generar números decimales aleatorios en un rango específico, útil para precios, coordenadas, medidas y cualquier valor continuo.

### ¿Por qué este ejercicio?
- **Decimales**: Generar valores decimales aleatorios
- **Rangos específicos**: Controlar el rango de valores
- **Aplicación práctica**: Precios, coordenadas, medidas
- **Continuidad**: Valores continuos, no discretos

### En el mundo real:
Cuando un sistema genera precios aleatorios para productos de prueba, usa uniform() para generar valores entre un mínimo y máximo. Sin uniform(), tendrías que usar random() y escalar manualmente.`,
      keyPoints: [
        'random.uniform(a, b) genera decimales en un rango',
        'a y b pueden ser cualquier número decimal',
        'El resultado puede ser cualquier valor entre a y b',
        'Útil para valores continuos'
      ],
      realWorldExample: 'En un sistema de simulaciones, uniform() genera coordenadas aleatorias: random.uniform(0.0, 100.0) para posiciones.'
    },
    theory: `# Generar Números con uniform()

## ¿Qué es uniform()?

**uniform()** genera números decimales aleatorios en un rango específico.

## Sintaxis

\`\`\`python
import random
precio = random.uniform(10.0, 20.0)  # Entre 10.0 y 20.0
\`\`\`

## Características

### Rango Continuo
\`uniform()\` puede generar cualquier valor en el rango:
\`\`\`python
valor = random.uniform(0.0, 1.0)  # Puede ser 0.0, 1.0, o cualquier valor entre ellos
\`\`\`

### Límites Inclusivos
Ambos límites pueden ser generados:
\`\`\`python
valor = random.uniform(10.0, 20.0)  # Puede ser 10.0, 20.0, o cualquier valor entre ellos
\`\`\`

## Comparación con randint()

- **randint()**: Enteros discretos
- **uniform()**: Decimales continuos

## Aplicaciones

- **Precios**: Generar precios aleatorios
- **Coordenadas**: Posiciones aleatorias
- **Medidas**: Valores continuos`,
    tips: [
      'random.uniform(a, b) genera decimales en un rango',
      'a y b pueden ser cualquier número decimal',
      'El resultado puede ser cualquier valor entre a y b',
      'Útil para valores continuos',
      'Diferente de randint() que genera enteros'
    ],
    advice: [
      'Usa uniform() para valores decimales',
      'Recuerda que genera valores continuos',
      'Practica en contextos de precios y coordenadas',
      'Compara con randint() para entender la diferencia'
    ]
  },
  {
    id: 5,
    title: 'Reproducibilidad con seed',
    description: 'Aprende a usar seed() para hacer que los números aleatorios sean reproducibles.',
    initialCode: `import random

# Establece una semilla para reproducibilidad
# Usa random.seed(valor) antes de generar números aleatorios
# Completa: random.seed(42)

# Genera números aleatorios
numero1 = random.randint(1, 10)
numero2 = random.randint(1, 10)

print(f"Número 1: {numero1}")
print(f"Número 2: {numero2}")

# Si ejecutas esto de nuevo con la misma semilla, obtendrás los mismos números`,
    solution: `import random

# Establece una semilla para reproducibilidad
# Usa random.seed(valor) antes de generar números aleatorios
# Completa: random.seed(42)
random.seed(42)

# Genera números aleatorios
numero1 = random.randint(1, 10)
numero2 = random.randint(1, 10)

print(f"Número 1: {numero1}")
print(f"Número 2: {numero2}")

# Si ejecutas esto de nuevo con la misma semilla, obtendrás los mismos números`,
    testCases: [],
    hints: [
      'random.seed(valor) establece la semilla',
      'Con la misma semilla, obtienes la misma secuencia',
      'Útil para debugging y testing',
      'Sin seed(), cada ejecución es diferente'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar seed()?',
      content: `**seed()** permite hacer que los números aleatorios sean reproducibles, esencial para debugging, testing y simulaciones que necesitan resultados consistentes.

### ¿Por qué este ejercicio?
- **Debugging**: Reproducir errores con la misma secuencia aleatoria
- **Testing**: Tests reproducibles con datos aleatorios
- **Simulaciones**: Comparar resultados con la misma semilla
- **Aplicación práctica**: Muy útil en desarrollo y testing

### En el mundo real:
Cuando un sistema de testing genera datos aleatorios, usa seed() para garantizar que los tests sean reproducibles. Sin seed(), cada ejecución generaría datos diferentes, haciendo difícil reproducir bugs.`,
      keyPoints: [
        'random.seed(valor) establece la semilla',
        'Con la misma semilla, obtienes la misma secuencia',
        'Útil para debugging y testing',
        'Sin seed(), cada ejecución es diferente'
      ],
      realWorldExample: 'En un sistema de testing, seed() garantiza que los tests con datos aleatorios sean reproducibles.'
    },
    theory: `# Reproducibilidad con seed()

## ¿Qué es seed()?

**seed()** establece la semilla para el generador de números aleatorios, haciendo que la secuencia sea reproducible.

## Sintaxis

\`\`\`python
import random
random.seed(42)  # Establece semilla
numero = random.randint(1, 10)  # Siempre el mismo con seed(42)
\`\`\`

## Reproducibilidad

Con la misma semilla, obtienes la misma secuencia:
\`\`\`python
random.seed(42)
a = random.randint(1, 10)  # Siempre el mismo valor
b = random.randint(1, 10)  # Siempre el mismo valor
\`\`\`

## Aplicaciones

- **Debugging**: Reproducir errores
- **Testing**: Tests reproducibles
- **Simulaciones**: Comparar resultados
- **Documentación**: Ejemplos consistentes`,
    tips: [
      'random.seed(valor) establece la semilla',
      'Con la misma semilla, obtienes la misma secuencia',
      'Útil para debugging y testing',
      'Sin seed(), cada ejecución es diferente',
      'Puedes usar cualquier número como semilla'
    ],
    advice: [
      'Usa seed() para debugging y testing',
      'Recuerda que hace la secuencia reproducible',
      'Practica en contextos de testing',
      'Compara resultados con y sin seed()'
    ]
  },
  {
    id: 6,
    title: 'Generar lista de números aleatorios',
    description: 'Aprende a generar una lista de números aleatorios usando comprensión de listas con random.',
    initialCode: `import random

# Genera una lista de 10 números aleatorios entre 1 y 100
# Usa comprensión de listas con random.randint()
numeros_aleatorios = 

print(f"Números aleatorios: {numeros_aleatorios}")`,
    solution: `import random

# Genera una lista de 10 números aleatorios entre 1 y 100
# Usa comprensión de listas con random.randint()
numeros_aleatorios = [random.randint(1, 100) for _ in range(10)]

print(f"Números aleatorios: {numeros_aleatorios}")`,
    testCases: [],
    hints: [
      'Usa comprensión de listas: [random.randint(1, 100) for _ in range(10)]',
      '_ se usa cuando no necesitas el valor del índice',
      'range(10) genera 10 iteraciones',
      'Cada elemento es un número aleatorio independiente'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué generar listas aleatorias?',
      content: `Generar **listas de números aleatorios** es útil para datos de prueba, simulaciones, y cualquier aplicación que necesite múltiples valores aleatorios.

### ¿Por qué este ejercicio?
- **Datos de prueba**: Generar datos aleatorios para testing
- **Simulaciones**: Múltiples valores aleatorios
- **Aplicación práctica**: Muy común en desarrollo
- **Eficiencia**: Generar múltiples valores de una vez

### En el mundo real:
Cuando un sistema genera datos de prueba, crea listas de valores aleatorios: [random.randint(1, 100) for _ in range(100)]. Sin comprensión de listas, necesitarías un bucle for más largo.`,
      keyPoints: [
        'Usa comprensión de listas con random',
        '[random.randint(a, b) for _ in range(n)]',
        '_ se usa cuando no necesitas el índice',
        'Cada elemento es independiente'
      ],
      realWorldExample: 'En un sistema de testing, se generan listas de IDs aleatorios: [random.randint(1000, 9999) for _ in range(50)].'
    },
    theory: `# Generar Listas de Números Aleatorios

## Comprensión de Listas con random

Puedes combinar comprensión de listas con random:
\`\`\`python
import random
numeros = [random.randint(1, 100) for _ in range(10)]
\`\`\`

## Características

### Independencia
Cada elemento es independiente:
\`\`\`python
numeros = [random.randint(1, 10) for _ in range(5)]
# Cada número es aleatorio independientemente
\`\`\`

### Variable _
Usa \`_\` cuando no necesitas el índice:
\`\`\`python
[random.randint(1, 10) for _ in range(5)]
\`\`\`

## Comparación

### Sin Comprensión
\`\`\`python
numeros = []
for _ in range(10):
    numeros.append(random.randint(1, 100))
\`\`\`

### Con Comprensión
\`\`\`python
numeros = [random.randint(1, 100) for _ in range(10)]
\`\`\`

## Aplicaciones

- **Datos de prueba**: Generar datos aleatorios
- **Simulaciones**: Múltiples valores aleatorios
- **Testing**: Casos de prueba aleatorios`,
    tips: [
      'Usa comprensión de listas con random',
      '[random.randint(a, b) for _ in range(n)]',
      '_ se usa cuando no necesitas el índice',
      'Cada elemento es independiente',
      'Más conciso que bucles for'
    ],
    advice: [
      'Practica generando listas aleatorias',
      'Experimenta con diferentes rangos y tamaños',
      'Aprende a usar _ cuando no necesitas el índice',
      'Practica en contextos de testing y simulaciones'
    ]
  },
  {
    id: 7,
    title: 'Simular lanzamiento de dados',
    description: 'Aprende a simular lanzamientos de dados usando random para crear simulaciones de juegos.',
    initialCode: `import random

# Simula 10 lanzamientos de un dado de 6 caras
# Genera números aleatorios entre 1 y 6
lanzamientos = 

# Calcula la suma de todos los lanzamientos
suma = 

print(f"Lanzamientos: {lanzamientos}")
print(f"Suma: {suma}")`,
    solution: `import random

# Simula 10 lanzamientos de un dado de 6 caras
# Genera números aleatorios entre 1 y 6
lanzamientos = [random.randint(1, 6) for _ in range(10)]

# Calcula la suma de todos los lanzamientos
suma = sum(lanzamientos)

print(f"Lanzamientos: {lanzamientos}")
print(f"Suma: {suma}")`,
    testCases: [],
    hints: [
      'Un dado de 6 caras genera números del 1 al 6',
      'Usa random.randint(1, 6) para cada lanzamiento',
      'Genera múltiples lanzamientos con comprensión de listas',
      'Suma todos los lanzamientos con sum()'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué simular lanzamientos de dados?',
      content: `Simular **lanzamientos de dados** es un ejemplo práctico de cómo usar aleatoriedad para modelar eventos del mundo real, útil para juegos, simulaciones y análisis probabilístico.

### ¿Por qué este ejercicio?
- **Simulaciones**: Modelar eventos aleatorios del mundo real
- **Juegos**: Implementar mecánicas de juegos
- **Aplicación práctica**: Ejemplo concreto y comprensible
- **Análisis**: Calcular estadísticas de simulaciones

### En el mundo real:
Cuando un sistema de juegos simula lanzamientos de dados, genera números aleatorios entre 1 y 6. Sin aleatoriedad, no podrías simular eventos aleatorios del mundo real.`,
      keyPoints: [
        'Un dado de 6 caras genera números del 1 al 6',
        'random.randint(1, 6) simula un lanzamiento',
        'Puedes generar múltiples lanzamientos',
        'Útil para simulaciones y juegos'
      ],
      realWorldExample: 'En un sistema de juegos de mesa, se simulan lanzamientos de dados: [random.randint(1, 6) for _ in range(2)] para dos dados.'
    },
    theory: `# Simular Lanzamientos de Dados

## Simulación de Dados

Puedes simular lanzamientos de dados usando random:
\`\`\`python
import random
lanzamiento = random.randint(1, 6)  # Dado de 6 caras
\`\`\`

## Múltiples Lanzamientos

Genera múltiples lanzamientos:
\`\`\`python
lanzamientos = [random.randint(1, 6) for _ in range(10)]
\`\`\`

## Análisis

Puedes calcular estadísticas:
\`\`\`python
lanzamientos = [random.randint(1, 6) for _ in range(100)]
suma = sum(lanzamientos)
promedio = suma / len(lanzamientos)
\`\`\`

## Aplicaciones

- **Juegos**: Simular mecánicas de juegos
- **Simulaciones**: Modelar eventos aleatorios
- **Análisis**: Calcular probabilidades`,
    tips: [
      'Un dado de 6 caras genera números del 1 al 6',
      'random.randint(1, 6) simula un lanzamiento',
      'Puedes generar múltiples lanzamientos',
      'Calcula estadísticas con sum() y promedio',
      'Útil para simulaciones y juegos'
    ],
    advice: [
      'Practica simulando diferentes tipos de dados',
      'Experimenta con diferentes números de lanzamientos',
      'Calcula estadísticas de tus simulaciones',
      'Practica en contextos de juegos y simulaciones'
    ]
  },
  {
    id: 8,
    title: 'Generar contraseña aleatoria',
    description: 'Aprende a generar contraseñas aleatorias combinando random.choice() con strings.',
    initialCode: `import random
import string

# Define caracteres disponibles para la contraseña
caracteres = string.ascii_letters + string.digits

# Genera una contraseña de 8 caracteres aleatorios
# Usa random.choice() en una comprensión de listas
# Luego une los caracteres con ''.join()
contraseña = 

print(f"Contraseña: {contraseña}")`,
    solution: `import random
import string

# Define caracteres disponibles para la contraseña
caracteres = string.ascii_letters + string.digits

# Genera una contraseña de 8 caracteres aleatorios
# Usa random.choice() en una comprensión de listas
# Luego une los caracteres con ''.join()
contraseña = ''.join([random.choice(caracteres) for _ in range(8)])

print(f"Contraseña: {contraseña}")`,
    testCases: [],
    hints: [
      'string.ascii_letters contiene letras mayúsculas y minúsculas',
      'string.digits contiene números del 0 al 9',
      'random.choice(caracteres) elige un carácter aleatorio',
      'Usa join() con string vacío para unir los caracteres'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué generar contraseñas aleatorias?',
      content: `Generar **contraseñas aleatorias** es una aplicación práctica común de aleatoriedad, útil para sistemas de seguridad, generación de tokens y creación de identificadores únicos.

### ¿Por qué este ejercicio?
- **Seguridad**: Generar contraseñas seguras
- **Aplicación práctica**: Muy común en sistemas reales
- **Combinación de conceptos**: Aleatoriedad + strings + listas
- **Utilidad**: Habilidad práctica valiosa

### En el mundo real:
Cuando un sistema genera contraseñas temporales o tokens de seguridad, usa aleatoriedad para crear strings únicos. Sin aleatoriedad, no podrías generar contraseñas seguras.`,
      keyPoints: [
        'string.ascii_letters contiene letras',
        'string.digits contiene números',
        'random.choice() elige caracteres aleatorios',
        'join() une caracteres en un string'
      ],
      realWorldExample: 'En un sistema de usuarios, se generan tokens de verificación usando join() con comprensión de listas.'
    },
    theory: `# Generar Contraseñas Aleatorias

## Combinar random con strings

Puedes generar contraseñas aleatorias combinando random con strings:
\`\`\`python
import random
import string

caracteres = string.ascii_letters + string.digits
contraseña = ''.join([random.choice(caracteres) for _ in range(8)])
\`\`\`

## Módulo string

Python incluye el módulo \`string\` con constantes útiles:
- \`string.ascii_letters\`: Letras mayúsculas y minúsculas
- \`string.digits\`: Números del 0 al 9
- \`string.punctuation\`: Caracteres especiales

## Unir Caracteres

Usa \`join()\` para unir caracteres:
\`\`\`python
caracteres = ['a', 'b', 'c']
string = ''.join(caracteres)  # "abc"
\`\`\`

## Aplicaciones

- **Seguridad**: Contraseñas y tokens
- **Identificadores**: IDs únicos
- **Testing**: Datos de prueba`,
    tips: [
      'string.ascii_letters contiene letras',
      'string.digits contiene números',
      'random.choice() elige caracteres aleatorios',
      'join() une caracteres en un string',
      'Puedes agregar string.punctuation para caracteres especiales'
    ],
    advice: [
      'Practica generando contraseñas de diferentes longitudes',
      'Experimenta con diferentes conjuntos de caracteres',
      'Aprende a usar el módulo string',
      'Practica en contextos de seguridad y testing'
    ]
  },
  {
    id: 9,
    title: 'Simular probabilidades con random',
    description: 'Aprende a simular eventos probabilísticos usando random para modelar situaciones del mundo real.',
    initialCode: `import random

# Simula lanzar una moneda 100 veces
# 0 = Cara, 1 = Cruz
# Usa random.randint(0, 1) para cada lanzamiento
lanzamientos = 

# Cuenta cuántas veces salió Cara (0)
caras = 

# Cuenta cuántas veces salió Cruz (1)
cruces = 

print(f"Caras: {caras}, Cruces: {cruces}")`,
    solution: `import random

# Simula lanzar una moneda 100 veces
# 0 = Cara, 1 = Cruz
# Usa random.randint(0, 1) para cada lanzamiento
lanzamientos = [random.randint(0, 1) for _ in range(100)]

# Cuenta cuántas veces salió Cara (0)
caras = sum(1 for l in lanzamientos if l == 0)

# Cuenta cuántas veces salió Cruz (1)
cruces = sum(1 for l in lanzamientos if l == 1)

print(f"Caras: {caras}, Cruces: {cruces}")`,
    testCases: [],
    hints: [
      'random.randint(0, 1) simula un lanzamiento de moneda',
      '0 puede representar Cara, 1 puede representar Cruz',
      'Genera múltiples lanzamientos con comprensión de listas',
      'Cuenta ocurrencias usando sum() con condición'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué simular probabilidades?',
      content: `Simular **eventos probabilísticos** permite modelar situaciones del mundo real, entender distribuciones probabilísticas y realizar análisis estadístico.

### ¿Por qué este ejercicio?
- **Probabilidades**: Modelar eventos aleatorios del mundo real
- **Análisis**: Entender distribuciones y frecuencias
- **Aplicación práctica**: Simulaciones, juegos, análisis estadístico
- **Fundamentos**: Base para estadística y probabilidad

### En el mundo real:
Cuando un sistema simula eventos probabilísticos (como lanzamientos de moneda, resultados de juegos, o eventos aleatorios), usa random para modelar estas situaciones. Sin simulaciones, tendrías que calcular probabilidades teóricamente.`,
      keyPoints: [
        'random.randint(0, 1) simula eventos binarios',
        'Puedes modelar cualquier evento probabilístico',
        'Genera múltiples simulaciones para análisis',
        'Cuenta ocurrencias para calcular frecuencias'
      ],
      realWorldExample: 'En un sistema de análisis, se simulan eventos probabilísticos para calcular probabilidades empíricas.'
    },
    theory: `# Simular Probabilidades con random

## Eventos Probabilísticos

Puedes simular eventos probabilísticos usando random:
\`\`\`python
import random
lanzamiento = random.randint(0, 1)  # Moneda: 0=Cara, 1=Cruz
\`\`\`

## Múltiples Simulaciones

Genera múltiples simulaciones:
\`\`\`python
lanzamientos = [random.randint(0, 1) for _ in range(100)]
\`\`\`

## Análisis de Frecuencias

Calcula frecuencias:
\`\`\`python
caras = sum(1 for l in lanzamientos if l == 0)
cruces = sum(1 for l in lanzamientos if l == 1)
\`\`\`

## Aplicaciones

- **Simulaciones**: Modelar eventos aleatorios
- **Análisis**: Calcular probabilidades empíricas
- **Juegos**: Mecánicas probabilísticas
- **Estadística**: Análisis de distribuciones`,
    tips: [
      'random.randint(0, 1) simula eventos binarios',
      'Puedes modelar cualquier evento probabilístico',
      'Genera múltiples simulaciones para análisis',
      'Cuenta ocurrencias para calcular frecuencias',
      'Útil para entender probabilidades empíricas'
    ],
    advice: [
      'Practica simulando diferentes eventos probabilísticos',
      'Experimenta con diferentes números de simulaciones',
      'Calcula frecuencias y compara con probabilidades teóricas',
      'Practica en contextos de análisis estadístico'
    ]
  },
  {
    id: 10,
    title: 'Generar datos aleatorios para testing',
    description: 'Aprende a generar datos aleatorios estructurados para usar en testing y desarrollo.',
    initialCode: `import random

# Genera una lista de diccionarios con datos aleatorios de usuarios
# Cada usuario debe tener: nombre (string aleatorio), edad (entre 18 y 65), salario (entre 1000 y 5000)
nombres_base = ["Ana", "Juan", "María", "Pedro", "Luis"]

usuarios = []
for i in range(5):
    usuario = {
        "nombre": ,
        "edad": ,
        "salario": 
    }
    usuarios.append(usuario)

print(f"Usuarios: {usuarios}")`,
    solution: `import random

# Genera una lista de diccionarios con datos aleatorios de usuarios
# Cada usuario debe tener: nombre (string aleatorio), edad (entre 18 y 65), salario (entre 1000 y 5000)
nombres_base = ["Ana", "Juan", "María", "Pedro", "Luis"]

usuarios = []
for i in range(5):
    usuario = {
        "nombre": random.choice(nombres_base),
        "edad": random.randint(18, 65),
        "salario": random.randint(1000, 5000)
    }
    usuarios.append(usuario)

print(f"Usuarios: {usuarios}")`,
    testCases: [],
    hints: [
      'random.choice() elige nombres aleatorios',
      'random.randint(18, 65) genera edades',
      'random.randint(1000, 5000) genera salarios',
      'Crea diccionarios con estos valores aleatorios'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué generar datos aleatorios para testing?',
      content: `Generar **datos aleatorios estructurados** es esencial para testing, desarrollo y simulaciones. Permite crear datos de prueba realistas sin datos reales.

### ¿Por qué este ejercicio?
- **Testing**: Crear datos de prueba variados
- **Desarrollo**: Probar con diferentes escenarios
- **Aplicación práctica**: Muy común en desarrollo profesional
- **Eficiencia**: Generar datos rápidamente

### En el mundo real:
Cuando un desarrollador necesita probar una funcionalidad con diferentes datos, genera datos aleatorios estructurados. Sin generación de datos, tendrías que crear manualmente cada caso de prueba.`,
      keyPoints: [
        'Genera datos aleatorios estructurados',
        'Combina diferentes funciones de random',
        'Crea estructuras de datos realistas',
        'Útil para testing y desarrollo'
      ],
      realWorldExample: 'En un sistema de testing, se generan usuarios aleatorios: [{"nombre": random.choice(nombres), "edad": random.randint(18, 65)} for _ in range(100)].'
    },
    theory: `# Generar Datos Aleatorios para Testing

## Datos Estructurados Aleatorios

Puedes generar datos aleatorios estructurados:
\`\`\`python
import random
usuario = {
    "nombre": random.choice(["Ana", "Juan"]),
    "edad": random.randint(18, 65),
    "salario": random.randint(1000, 5000)
}
\`\`\`

## Múltiples Registros

Genera múltiples registros:
\`\`\`python
usuarios = []
for i in range(10):
    usuario = {
        "nombre": random.choice(nombres),
        "edad": random.randint(18, 65)
    }
    usuarios.append(usuario)
\`\`\`

## Aplicaciones

- **Testing**: Datos de prueba variados
- **Desarrollo**: Probar con diferentes escenarios
- **Simulaciones**: Datos realistas
- **Prototipado**: Rápido desarrollo`,
    tips: [
      'Genera datos aleatorios estructurados',
      'Combina diferentes funciones de random',
      'Crea estructuras de datos realistas',
      'Útil para testing y desarrollo',
      'Puedes usar comprensión de listas para múltiples registros'
    ],
    advice: [
      'Practica generando diferentes tipos de datos aleatorios',
      'Experimenta con diferentes estructuras',
      'Aprende a crear datos realistas',
      'Practica en contextos de testing y desarrollo'
    ]
  }
];
